import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { AdminLayout } from "./AdminDashboard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell
} from "recharts";
import { 
  Ticket, DollarSign, Users, TrendingUp, Calendar, 
  Download, RefreshCw, CheckCircle, Clock
} from "lucide-react";
import { format } from "date-fns";

const COLORS = ['#1e3a5f', '#60a5fa', '#3b82f6', '#93c5fd', '#2563eb'];

export default function AdminTicketSales() {
  const [salesData, setSalesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    fetchSalesData();
  }, []);

  const fetchSalesData = async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/ticket-sales`;
      const params = [];
      if (startDate) params.push(`start_date=${startDate}`);
      if (endDate) params.push(`end_date=${endDate}`);
      if (params.length) url += `?${params.join("&")}`;
      
      const response = await axios.get(url, { withCredentials: true });
      setSalesData(response.data);
    } catch (error) {
      console.error("Error fetching sales data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };

  const exportReport = async () => {
    window.open(`${API}/admin/reports/tickets?format=csv`, '_blank');
  };

  if (loading) {
    return (
      <AdminLayout currentPage="Ticket Sales">
        <div className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-slate-100 rounded-lg animate-pulse"></div>
            ))}
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout currentPage="Ticket Sales">
      <div className="p-8" data-testid="admin-ticket-sales">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-heading-2 text-deep-blue">Ticket Sales Tracking</h1>
            <p className="text-slate-600">Monitor ticket sales and check-in rates</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={fetchSalesData} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={exportReport} className="btn-primary">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Date Filters */}
        <div className="bg-white rounded-lg p-4 mb-6 flex flex-wrap gap-4 items-end">
          <div>
            <Label>Start Date</Label>
            <Input 
              type="date" 
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-40"
            />
          </div>
          <div>
            <Label>End Date</Label>
            <Input 
              type="date" 
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-40"
            />
          </div>
          <Button onClick={fetchSalesData}>Apply Filters</Button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-deep-blue to-deep-blue/80 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/70 text-sm">Total Revenue</p>
                  <p className="text-3xl font-bold font-heading mt-1">
                    {formatCurrency(salesData?.summary?.total_revenue || 0)}
                  </p>
                </div>
                <div className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center">
                  <DollarSign className="w-7 h-7 text-awareness-blue" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Total Tickets</p>
                  <p className="text-3xl font-bold font-heading text-deep-blue mt-1">
                    {salesData?.summary?.total_tickets || 0}
                  </p>
                </div>
                <div className="w-14 h-14 bg-awareness-blue/10 rounded-full flex items-center justify-center">
                  <Ticket className="w-7 h-7 text-awareness-blue" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Checked In</p>
                  <p className="text-3xl font-bold font-heading text-deep-blue mt-1">
                    {salesData?.summary?.tickets_used || 0}
                  </p>
                </div>
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-7 h-7 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Check-in Rate</p>
                  <p className="text-3xl font-bold font-heading text-deep-blue mt-1">
                    {salesData?.summary?.check_in_rate || 0}%
                  </p>
                </div>
                <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-7 h-7 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Daily Sales Trend */}
          <Card>
            <CardHeader>
              <CardTitle className="text-deep-blue">Daily Sales Trend</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={salesData?.daily_trend || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" stroke="#64748b" fontSize={12} />
                    <YAxis stroke="#64748b" fontSize={12} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="count" name="Tickets" stroke="#1e3a5f" strokeWidth={2} />
                    <Line type="monotone" dataKey="revenue" name="Revenue ($)" stroke="#60a5fa" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Sales by Event */}
          <Card>
            <CardHeader>
              <CardTitle className="text-deep-blue">Sales by Event</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={salesData?.by_event || []} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" stroke="#64748b" fontSize={12} />
                    <YAxis dataKey="event_title" type="category" stroke="#64748b" fontSize={11} width={120} />
                    <Tooltip formatter={(value, name) => [name === 'revenue' ? formatCurrency(value) : value, name]} />
                    <Legend />
                    <Bar dataKey="tickets_sold" name="Tickets Sold" fill="#1e3a5f" radius={[0, 4, 4, 0]} />
                    <Bar dataKey="tickets_used" name="Checked In" fill="#60a5fa" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Event Details Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-deep-blue">Event Sales Details</CardTitle>
          </CardHeader>
          <CardContent>
            {salesData?.by_event && salesData.by_event.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-3 font-semibold text-slate-700">Event</th>
                      <th className="text-left p-3 font-semibold text-slate-700">Date</th>
                      <th className="text-right p-3 font-semibold text-slate-700">Tickets Sold</th>
                      <th className="text-right p-3 font-semibold text-slate-700">Checked In</th>
                      <th className="text-right p-3 font-semibold text-slate-700">Revenue</th>
                      <th className="text-right p-3 font-semibold text-slate-700">Check-in Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salesData.by_event.map((event, idx) => (
                      <tr key={event.event_id} className="border-b hover:bg-slate-50">
                        <td className="p-3 font-medium text-deep-blue">{event.event_title}</td>
                        <td className="p-3 text-slate-600">
                          {event.event_date ? format(new Date(event.event_date), "MMM d, yyyy") : "-"}
                        </td>
                        <td className="p-3 text-right">{event.tickets_sold}</td>
                        <td className="p-3 text-right">{event.tickets_used}</td>
                        <td className="p-3 text-right font-semibold text-awareness-blue">
                          {formatCurrency(event.revenue)}
                        </td>
                        <td className="p-3 text-right">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            (event.tickets_used / event.tickets_sold * 100) >= 80 
                              ? 'bg-green-100 text-green-700'
                              : (event.tickets_used / event.tickets_sold * 100) >= 50
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-slate-100 text-slate-700'
                          }`}>
                            {event.tickets_sold > 0 
                              ? Math.round(event.tickets_used / event.tickets_sold * 100) 
                              : 0}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Ticket className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No ticket sales data available</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
